// FIXME: https://github.com/import-js/eslint-plugin-import/pull/2716
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, { useCallback, useState } from 'react';

import { useStore } from './context/provider';
import {
  PaymentCardUnsupportedError,
  PaymentConfirmationError,
  PaymentGeneralError,
  PaymentSavedCreditCardPayError,
  PaymentsForAdBudget,
  type PaymentMethodType,
  type TrackingAdapter,
} from '@seek/payments-widget';
import { CheckoutActionType } from './context/reducer';
import { Box, Stack } from 'braid-design-system';
import { HirerOrderSummary } from '@seek/hirer-ecommerce';
import { getLogger } from '../../utils/logger/logger';

import type { Locale } from '@seek/melways-sites';

import { useTranslations } from '@vocab/react';
import { useAppContext } from 'src/App/context/AppContext';
import { PaymentOption } from 'src/App/pages/checkout/paymentOptions';
import translations from 'src/App/pages/checkout/.vocab/index';
import { createCheckoutPageOnLoadBaseEvent } from './eventTracking/createEvents';
import { CheckoutErrorSection } from 'src/App/pages/checkout/ErrorSection/CheckoutErrorSection';
import { useTracking } from 'src/App/context/TrackingProvider';
import { useQueryAccountInfo } from '../../hooks/useQueryAccountInfo.ts';

interface AdBudgetCheckoutProps {
  billToAgent: boolean;
  country: string;
  language: string;
  orderId: string;
  productSetId: string;
  userOnCreditHold: boolean;
  submitBusinessAddressForm?: () => void;
  validateBusinessAddressForm: () => Promise<boolean>;
}

const AdBudgetCheckout: React.FC<
  React.PropsWithChildren<AdBudgetCheckoutProps>
> = (props) => {
  const {
    billToAgent,
    country,
    language,
    orderId,
    productSetId,
    userOnCreditHold,
    submitBusinessAddressForm,
    validateBusinessAddressForm,
  } = props;
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(true);
  const {
    dispatch,
    state: {
      idempotencyKey,
      adBudgetInfo,
      adBudgetList,
      advertiserContracts,
      paymentMethodSelected,
    },
  } = useStore();
  const { trackLink, trackView } = useTracking();

  const { accountInfo } = useQueryAccountInfo();
  const advertiserId = accountInfo?.currentAdvertiser?.advertiserId;

  const { environment, site } = useAppContext();
  const env = environment === 'local' ? 'dev' : environment;
  const { t } = useTranslations(translations);

  const [isPaymentInProgress, setIsPaymentInProgress] =
    useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [confirmOrderError, setConfirmOrderError] = useState<boolean>(false);

  const onOrderSummaryLoading = useCallback((setLoading: boolean) => {
    setOrderSummaryLoading(setLoading);
  }, []);

  const onPaymentFailed = (error: any) => {
    setIsPaymentInProgress(false);

    const expectedError = [
      PaymentCardUnsupportedError,
      PaymentConfirmationError,
      PaymentGeneralError,
      PaymentSavedCreditCardPayError,
    ].find((err) => error instanceof err);

    if (expectedError) {
      getLogger().info('Payment failed with expected error', {
        error,
        activity: 'onPaymentFailed',
      });
      return;
    }

    throw error;
  };

  const handlePaymentOptionChange = (
    _selectedIndex: number,
    selectedItem?: string,
  ) => {
    if (!selectedItem) {
      return;
    }
    dispatch({
      type: CheckoutActionType.SET_PAYMENT_METHOD,
      paymentMethodSelected: selectedItem as PaymentMethodType,
    });
  };

  const onCheckoutClicked = async ({
    paymentMethod,
    isSavedCreditCard,
  }: {
    paymentMethod: PaymentMethodType;
    isSavedCreditCard?: boolean;
  }) => {
    if (isPaymentInProgress) {
      return;
    }
    setIsPaymentInProgress(true);

    if (submitBusinessAddressForm) {
      const validBusinessAddressForm = await validateBusinessAddressForm();
      if (!Boolean(validBusinessAddressForm)) {
        setIsPaymentInProgress(false);
        throw new Error('Invalid business address form'); // We throw this error so that payment widget will not proceed with checkout
      }
    }

    dispatch({
      type: CheckoutActionType.SET_IS_PAID_BY_STORED_CREDIT_CARD,
      isPaidByStoredCreditCard:
        Boolean(isSavedCreditCard) && paymentMethod === 'CreditCard',
    });

    if (userOnCreditHold) {
      getLogger().error('Hirer on credit hold', {
        activity: 'processPrepayment',
      });
      throw new Error('Hirer on credit hold'); // We throw this error so that payment widget will not proceed with checkout
    }
  };

  const onCheckoutFailed = (error: Error, isHandled?: boolean) => {
    setIsPaymentInProgress(false);

    if (isHandled) {
      return;
    }

    getLogger().error('Confirm Order Error', {
      error,
      activity: 'confirmOrder',
    });
    setConfirmOrderError(true);
  };

  const handledCancelPayment = () => {
    setIsPaymentInProgress(false);
  };

  const onReplaceSavedCard = () => {
    dispatch({
      type: CheckoutActionType.SET_PAYMENT_METHOD,
      paymentMethodSelected: PaymentOption.CREDIT_CARD,
    });
  };

  const onSavedCardLoaded = () => {
    dispatch({
      type: CheckoutActionType.SET_PAYMENT_METHOD,
      paymentMethodSelected: PaymentOption.CREDIT_CARD,
    });
  };

  const onPaymentSuccess = () => {
    if (submitBusinessAddressForm) {
      submitBusinessAddressForm();
    }
    dispatch({
      type: CheckoutActionType.SET_CONFIRMED_INVOICE_ORDER,
      orderId: orderId!,
    });
  };

  const onSaveCardOptionChanged = (shouldSave: boolean) => {
    dispatch({
      type: CheckoutActionType.SET_IS_SAVE_CREDIT_CARD_CHECKED,
      isSaveCreditCardChecked: shouldSave,
    });
  };

  const onLoadBaseEvent = createCheckoutPageOnLoadBaseEvent(
    {
      advertiserContracts,
      productEvents: 'checkout',
      productSetId,
      adBudgetList,
    },
    adBudgetInfo!,
  );

  const paymentWidgetTrackingAdapter: TrackingAdapter = {
    sendLinkEvent: (eventName, eventAttributes) => {
      trackLink({
        eventName,
        data: { ...onLoadBaseEvent, ...eventAttributes },
      });
    },
    sendPageEvent: (eventAttributes) =>
      // payment widget does not use page view events
      trackView({
        eventName: '',
        data: { ...onLoadBaseEvent, ...eventAttributes },
      }),
  };

  return (
    <Stack space="medium">
      {Boolean(orderId) && (
        <Box padding="large" background="surface">
          <HirerOrderSummary
            advertiserId={advertiserId}
            orderId={orderId!}
            advertiserNation={country}
            onLoading={onOrderSummaryLoading}
            logger={getLogger()}
            language={language as Locale}
          />
        </Box>
      )}

      {!orderSummaryLoading && (
        <PaymentsForAdBudget
          billToAgent={billToAgent}
          currency={adBudgetInfo!.currency}
          env={env}
          idempotencyKey={idempotencyKey!}
          onError={onPaymentFailed}
          hideAllPayments={false}
          onPaymentMethodChanged={handlePaymentOptionChange}
          onPostAdButtonClicked={onCheckoutClicked}
          onPostAdButtonClickFailed={onCheckoutFailed}
          handledCancelPayment={handledCancelPayment}
          onReplaceSavedCard={onReplaceSavedCard}
          onSaveCardOptionChanged={onSaveCardOptionChanged}
          onSavedCardLoaded={onSavedCardLoaded}
          onSuccess={onPaymentSuccess}
          orderId={orderId!}
          paymentMethod={paymentMethodSelected!}
          postAdButtonText={t('checkout')}
          site={site}
          trackingAdapter={paymentWidgetTrackingAdapter}
          useCase="ADBUDGET"
        />
      )}
      {confirmOrderError && <CheckoutErrorSection />}
    </Stack>
  );
};

export default AdBudgetCheckout;
